import { useCallback, useEffect, useRef, useState } from "react";

import { ApiError } from "../api/errors";
import {
  MAX_IMAGE_BYTES,
  MIN_IMAGE_BYTES,
  SUPPORTED_CONTENT_TYPES,
  imagesApi,
  putToSignedUrl,
  sha256Hex,
} from "../api/images";
import type { ProcessingJobStatus } from "../api/types";
import { useProductRefresh } from "./useProduct";

/** How long to leave between status reads. The backend job is seconds-long at
 *  best; anything faster is load without information. */
const POLL_INTERVAL_MS = 2000;

/** The stages an administrator actually sees. Each maps to real state — none is
 *  a guess, and no percentage is invented from them. */
export type ReplacementStage =
  | "idle"
  | "preparing"
  | "uploading"
  | "queued"
  | "processing"
  | "needs_review"
  | "completed"
  | "failed";

export interface ReplacementState {
  stage: ReplacementStage;
  error: unknown;
  warnings: string[];
  jobId: string | null;
}

const IDLE: ReplacementState = {
  stage: "idle",
  error: null,
  warnings: [],
  jobId: null,
};

/** ProcessingJob.status -> the stage shown. 'running' is the backend's name for
 *  the in-progress state; 'cancelled' is terminal and not a success. */
function stageForJob(status: ProcessingJobStatus): ReplacementStage {
  switch (status) {
    case "queued":
      return "queued";
    case "running":
      return "processing";
    case "needs_review":
      return "needs_review";
    case "completed":
      return "completed";
    case "failed":
    case "cancelled":
      return "failed";
  }
}

export function isTerminal(stage: ReplacementStage): boolean {
  return stage === "completed" || stage === "needs_review" || stage === "failed";
}

/** An obvious problem with the chosen file. The backend stays authoritative;
 *  this only avoids a pointless round trip. */
export function checkFile(file: File): string | null {
  if (file.size === 0) return "That file is empty.";
  if (file.size < MIN_IMAGE_BYTES) return "That file is too small to be an image.";
  if (file.size > MAX_IMAGE_BYTES) {
    return `That file is ${formatBytes(file.size)}. The maximum is ${formatBytes(
      MAX_IMAGE_BYTES,
    )}.`;
  }
  if (!(SUPPORTED_CONTENT_TYPES as readonly string[]).includes(file.type)) {
    return `${file.type || "That file type"} is not supported. Use AVIF, WebP, PNG or JPEG.`;
  }
  return null;
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * One replacement: initiate, PUT to storage, complete, then watch the job.
 *
 * Three properties this hook exists to guarantee:
 *
 *   * The signed URL never leaves this function. It is a local binding inside
 *     `start`, never stored in state, never cached by React Query, never
 *     written to browser storage and never placed in an error message.
 *   * Nothing is ever retried automatically. A failed PUT does not call
 *     complete-upload, and a failed complete does not re-upload — an
 *     administrator restarts the operation explicitly.
 *   * Polling stops the moment it is pointless: on a terminal status, on
 *     unmount, and when the session is gone.
 */
export function useImageReplacement(productId: string) {
  const refresh = useProductRefresh(productId);
  const [state, setState] = useState<ReplacementState>(IDLE);
  const busyRef = useRef(false);
  const mountedRef = useRef(true);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const stopPolling = useCallback(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      // Leaving the workspace ends the observation; nothing keeps running in
      // the background against a page the administrator has left.
      mountedRef.current = false;
      stopPolling();
    };
  }, [stopPolling]);

  const reset = useCallback(() => {
    stopPolling();
    busyRef.current = false;
    setState(IDLE);
  }, [stopPolling]);

  const poll = useCallback(
    (jobId: string) => {
      stopPolling();
      timerRef.current = setTimeout(() => {
        void (async () => {
          if (!mountedRef.current) return;
          try {
            const job = await imagesApi.processingJob(jobId);
            if (!mountedRef.current) return;
            const stage = stageForJob(job.status);
            const warnings = (job.report?.warnings ?? []).map(String);
            setState({ stage, error: null, warnings, jobId });

            if (isTerminal(stage)) {
              busyRef.current = false;
              // Completed and needs_review both produced real rows: the
              // product, its versions and its validation are all stale now.
              if (stage !== "failed") await refresh();
              else await refresh();
              return;
            }
            poll(jobId);
          } catch (caught) {
            if (!mountedRef.current) return;
            busyRef.current = false;
            // A vanished session ends observation rather than looping on 401.
            setState((prev) => ({ ...prev, stage: "failed", error: caught }));
          }
        })();
      }, POLL_INTERVAL_MS);
    },
    [refresh, stopPolling],
  );

  /**
   * @param draftVersion the version the caller currently believes the draft is
   * on. It is passed in rather than read here so a stale form cannot silently
   * publish over someone else's edit.
   */
  const start = useCallback(
    async (input: {
      file: File;
      imageRole: string;
      variantId: string;
      draftVersion: number;
    }) => {
      if (busyRef.current) return; // double-submit guard
      busyRef.current = true;
      setState({ stage: "preparing", error: null, warnings: [], jobId: null });

      try {
        const checksum = await sha256Hex(input.file);

        const initiated = await imagesApi.initiateUpload(productId, {
          expected_version: input.draftVersion,
          image_role: input.imageRole,
          variant_id: input.variantId,
          original_filename: input.file.name,
          content_type: input.file.type,
          file_size: input.file.size,
          ...(checksum ? { checksum } : {}),
        });

        // --- the signed URL lives only in this local binding ---
        setState((prev) => ({ ...prev, stage: "uploading" }));
        await putToSignedUrl(initiated, input.file);
        // A failed PUT throws above, so completion is unreachable without a
        // stored object — which is exactly the rule.

        const completion = await imagesApi.completeUpload(productId, {
          // initiate does not bump the draft; the version is still the one the
          // session was opened with.
          expected_version: initiated.revision_version,
          draft_image_upload_id: initiated.draft_image_upload_id,
        });

        if (!mountedRef.current) return;
        setState({
          stage: "queued",
          error: null,
          warnings: [],
          jobId: completion.processing_job_id,
        });
        // complete-upload bumped the draft version, so the page must reload
        // before any further mutation carries a token the server accepts.
        await refresh();
        poll(completion.processing_job_id);
      } catch (caught) {
        busyRef.current = false;
        if (!mountedRef.current) return;
        setState({ stage: "failed", error: caught, warnings: [], jobId: null });
        // A stale draft means the local copy is behind; reload so a fresh
        // attempt can carry the current version. Never resend on its own.
        if (caught instanceof ApiError && caught.isStale) await refresh();
      }
    },
    [productId, poll, refresh],
  );

  /**
   * A frame colour's FIRST photographs, sent as one set.
   *
   * WHY THIS IS NOT `start` CALLED THREE TIMES. A frame's listing card is
   * composed from front, side and angle together, so a colour that has never
   * been photographed has no set to carry the other two views forward from.
   * Completing one upload against source-set version 0 has nothing to compose
   * and the backend refuses it — correctly. The set is the unit of work, and it
   * is finished by the media API's own set-completion call.
   *
   * The three files are uploaded against the SAME draft version, because
   * initiating does not bump it; only completion does, once, for the whole set.
   * A failure anywhere leaves the colour unphotographed rather than half
   * photographed, and the operator chooses all three again.
   *
   * @param draftVersion as in `start`: what the caller believes the draft is on.
   */
  const startInitialSet = useCallback(
    async (input: {
      /** In the order the family names them, each under its upload role. */
      files: readonly { file: File; imageRole: string }[];
      variantId: string;
      draftVersion: number;
    }) => {
      if (busyRef.current) return; // double-submit guard
      busyRef.current = true;
      setState({ stage: "preparing", error: null, warnings: [], jobId: null });

      try {
        const uploadIds: string[] = [];
        for (const { file, imageRole } of input.files) {
          const checksum = await sha256Hex(file);
          const initiated = await imagesApi.initiateUpload(productId, {
            expected_version: input.draftVersion,
            image_role: imageRole,
            variant_id: input.variantId,
            original_filename: file.name,
            content_type: file.type,
            file_size: file.size,
            ...(checksum ? { checksum } : {}),
          });

          // --- the signed URL lives only in this local binding ---
          setState((prev) => ({ ...prev, stage: "uploading" }));
          await putToSignedUrl(initiated, file);
          uploadIds.push(initiated.draft_image_upload_id);
        }

        const completion = await imagesApi.completeInitialSet(productId, {
          // Still the version the sessions were opened with: initiating does
          // not bump the draft, and nothing has completed yet.
          expected_version: input.draftVersion,
          draft_image_upload_ids: uploadIds,
        });

        if (!mountedRef.current) return;
        setState({
          stage: "queued",
          error: null,
          warnings: [],
          jobId: completion.processing_job_id,
        });
        await refresh();
        poll(completion.processing_job_id);
      } catch (caught) {
        busyRef.current = false;
        if (!mountedRef.current) return;
        setState({ stage: "failed", error: caught, warnings: [], jobId: null });
        if (caught instanceof ApiError && caught.isStale) await refresh();
      }
    },
    [productId, poll, refresh],
  );

  return { state, start, startInitialSet, reset, busy: busyRef };
}
